import * as Sentry from '@sentry/react';

/**
 * Contexto de interface pro backend: em que TELA o usuário estava, qual
 * AÇÃO (botão, submit, tecla, arquivo escolhido) disparou a request e em que
 * SESSÃO. Vai em três headers (`X-Abbiamo-Screen`, `X-Abbiamo-Action`,
 * `X-Abbiamo-Session`) e o `@abbiamo/utils` do outro lado transforma em
 * `abbiamo.ui.screen`, `abbiamo.ui.action` e `abbiamo.session_id` em todo
 * log e span da request, repassando pros serviços que ele chama e pras
 * mensagens SQS.
 *
 * Trace ≠ sessão. Cada INTERAÇÃO abre um span do Sentry (`ui.action`,
 * "click: Anexar foto") que é a RAIZ do trace: as requests que ela dispara
 * vão com `traceparent` apontando pra ele, então o backend pendura embaixo
 * de um pai único em vez de virar N raízes soltas. Uma request pertence à
 * interação se começa logo depois dela (JOIN_AFTER_MS) ou enquanto outra
 * request da mesma interação ainda está rodando (a cascata "upload →
 * refetch"). Polling e timer que caem fora disso são `background`, sem
 * traceparent nosso (o Sentry continua com o dele).
 *
 * O span da interação diz O QUE foi clicado: `abbiamo.ui.kind` (click,
 * submit, enter, upload), `abbiamo.ui.label` (rótulo do botão),
 * `abbiamo.ui.element` (tag#id.classe[data-testid]), `abbiamo.ui.path` (a
 * URL real, com ids e query) e, no upload, quantos arquivos, de que tipo e
 * quantos bytes. O rótulo é curto e sem dado do usuário (rótulo do botão,
 * não o valor digitado; tipo e tamanho do arquivo, não o nome).
 *
 * "Tudo que esse usuário fez" é o filtro por `session_id`, não um trace
 * gigante.
 */
export const UI_SCREEN_HEADER = 'X-Abbiamo-Screen';
export const UI_ACTION_HEADER = 'X-Abbiamo-Action';
export const UI_SESSION_HEADER = 'X-Abbiamo-Session';

/** Request que começa até isto depois da interação entra no trace dela. */
const JOIN_AFTER_MS = 800;
/** Numa troca de tela, o mesmo vale por mais tempo (sessão, depois os dados). */
const NAVIGATION_JOIN_MS = 8_000;
/** Folga entre o fim de uma request da interação e o começo da próxima (cascata). */
const CASCADE_GAP_MS = 300;
/** Nunca segura uma interação aberta mais que isto. */
const MAX_INTERACTION_MS = 30_000;
/** O clique em "Anexar foto" abre o seletor; o arquivo chega até isto depois. */
const UPLOAD_AFTER_CLICK_MS = 120_000;
const MAX = 120;
const SESSION_KEY = 'abbiamo.session_id';

type Attrs = Record<string, string | number>;

interface Interaction {
  label: string;
  /** Janela em que uma request nova ainda entra nesta interação. */
  joinMs: number;
  startedAt: number;
  traceId: string;
  spanId: string;
  /** o span do Sentry foi amostrado: o backend pode apontar pra ele como pai */
  sampled: boolean;
  span?: { end: () => void };
  inflight: number;
  lastEndAt: number;
  ended: boolean;
}

let current: Interaction | undefined;
let lastScreen = '';
let sessionId = '';

/**
 * Relógio do servidor − relógio daqui, em ms. O backend devolve
 * `x-abbiamo-time` em toda resposta; ao estilo NTP, a hora dele é comparada
 * com o meio da ida e volta, e a amostra de menor RTT manda (é a menos
 * contaminada por rede). Sem isto, com o relógio da máquina do usuário
 * adiantado, o `handle` do backend aparece começando antes do clique.
 */
let clock: { offsetMs: number; rttMs: number; at: number } | undefined;
const CLOCK_MAX_AGE_MS = 10 * 60_000;

const learnClock = (sentAt: number, serverTimeMs: number): void => {
  const now = Date.now();
  const rtt = Math.max(0, now - sentAt);
  const offset = serverTimeMs - (sentAt + now) / 2;
  if (!clock || rtt <= clock.rttMs || now - clock.at > CLOCK_MAX_AGE_MS) {
    clock = { offsetMs: Math.round(offset), rttMs: rtt, at: now };
  }
};

/** Agora, na hora do servidor, em segundos (o que o Sentry espera). */
const serverNow = (): number => (Date.now() + (clock?.offsetMs ?? 0)) / 1000;

/** Só pra diagnóstico no console: `__abbiamoClock()`. */
if (typeof window !== 'undefined') (window as any).__abbiamoClock = () => clock;
/** O último clique com rótulo: é o nome do upload que ele abriu. */
let lastClick: { label: string; at: number } | undefined;

const clean = (s: string) => s.replace(/\s+/g, ' ').trim().slice(0, MAX);

/**
 * Valor de header HTTP: só ASCII imprimível. "Criar automação" vira
 * "Criar automacao" — o Node derruba a request inteira (ERR_INVALID_CHAR)
 * se um header carrega acento, e o serverless-express do backend faz isso
 * antes de qualquer log útil. O rótulo com acento continua no span.
 */
const headerSafe = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\x20-\x7e]/g, '?')
    .slice(0, MAX);

const hex = (bytes: number): string => {
  const buf = new Uint8Array(bytes);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < bytes; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
};

/** Uma sessão por aba: vive no sessionStorage, sobrevive a reload, morre com a aba. */
export const currentSessionId = (): string => {
  if (sessionId) return sessionId;
  if (typeof window === 'undefined') return '';
  try {
    const stored = window.sessionStorage.getItem(SESSION_KEY);
    if (stored) return (sessionId = stored);
    sessionId = hex(8);
    window.sessionStorage.setItem(SESSION_KEY, sessionId);
  } catch {
    sessionId = hex(8);
  }
  return sessionId;
};

/**
 * O elemento clicável (botão, link, role=button) que o alvo do evento está
 * dentro, e o rótulo dele: aria-label > data-action/data-testid > texto.
 */
const labelled = (
  el: Element | null,
): { label: string; node: Element } | undefined => {
  let node: Element | null = el;
  for (let i = 0; node && i < 5; i++) {
    const aria = node.getAttribute('aria-label');
    if (aria) return { label: clean(aria), node };
    const data = node.getAttribute('data-action') || node.getAttribute('data-testid');
    if (data) return { label: clean(data), node };
    const tag = node.tagName.toLowerCase();
    if (tag === 'button' || tag === 'a' || node.getAttribute('role') === 'button') {
      const text = (node as HTMLElement).innerText || node.textContent || '';
      const t = clean(text);
      if (t) return { label: t, node };
      const title = node.getAttribute('title');
      if (title) return { label: clean(title), node };
      return { label: tag, node };
    }
    node = node.parentElement;
  }
  return undefined;
};

/** `button#save.MuiButton-root.primary[data-testid=save]`: onde no DOM, curto. */
const selectorOf = (node: Element | null | undefined): string | undefined => {
  if (!node) return undefined;
  let s = node.tagName.toLowerCase();
  if (node.id) s += `#${node.id}`;
  const classes = Array.from(node.classList || [])
    .filter(c => c && !/^(css|sc|jss)-/.test(c))
    .slice(0, 2);
  if (classes.length) s += `.${classes.join('.')}`;
  const testId = node.getAttribute('data-testid') || node.getAttribute('data-action');
  if (testId) s += `[data-testid=${clean(testId).slice(0, 40)}]`;
  const type = node.getAttribute('type');
  if (type && !testId) s += `[type=${type}]`;
  return s.slice(0, 80);
};

/** A URL real (com ids e query), pra reproduzir; a tela mascarada vai em `screen`. */
const currentPath = (): string =>
  typeof window === 'undefined'
    ? ''
    : `${window.location.pathname}${window.location.search}`.slice(0, 200);

const endInteraction = (it: Interaction) => {
  if (it.ended) return;
  it.ended = true;
  try {
    (it.span as any)?.end(serverNow());
  } catch {
    /* noop */
  }
};

/**
 * Começa uma interação: rótulo + span raiz no Sentry. O contexto de
 * propagação recebe o mesmo `trace_id`, então erro capturado no browser
 * durante a interação cai no mesmo trace.
 */
const begin = (kind: string, label?: string, extra: Attrs = {}) => {
  if (current) endInteraction(current);
  const name = label ? `${kind}: ${label}` : kind;
  let traceId = hex(16);
  let spanId = hex(8);
  let sampled = false;
  let span: { end: () => void } | undefined;
  try {
    const attributes: Attrs = {
      'abbiamo.ui.screen': currentScreen(),
      'abbiamo.ui.action': name,
      'abbiamo.ui.kind': kind,
      'abbiamo.ui.path': currentPath(),
      'abbiamo.session_id': currentSessionId(),
      ...extra,
    };
    if (label) attributes['abbiamo.ui.label'] = label;
    // Trace NOVO por interação: sem o startNewTrace o span nasceria no trace
    // que o escopo já tinha (o do pageload) e toda interação da tela cairia
    // no mesmo trace_id.
    const open = () =>
      Sentry.startInactiveSpan({
        name,
        op: 'ui.action',
        forceTransaction: true,
        attributes,
        // Na hora do servidor: é o relógio que data os spans do backend.
        startTime: serverNow(),
      });
    const s: any =
      typeof (Sentry as any).startNewTrace === 'function'
        ? (Sentry as any).startNewTrace(open)
        : open();
    if (s && typeof s.spanContext === 'function') {
      const ctx = s.spanContext();
      if (ctx?.traceId && ctx?.spanId) {
        traceId = ctx.traceId;
        spanId = ctx.spanId;
        sampled = typeof s.isRecording === 'function' ? s.isRecording() : false;
        span = s;
      }
    }
    (Sentry.getCurrentScope() as any).setPropagationContext({
      traceId,
      sampleRand: Math.random(),
      sampled,
    });
  } catch {
    /* Sentry desligado (DEV/LOCAL): trace nosso, backend é a raiz */
  }
  current = {
    label: name,
    // Navegação espera mais: as requests da tela só saem depois da sessão
    // (/portal/me, /me), segundos depois do carregamento.
    joinMs: kind === 'navigation' ? NAVIGATION_JOIN_MS : JOIN_AFTER_MS,
    startedAt: Date.now(),
    traceId,
    spanId,
    sampled,
    span,
    inflight: 0,
    lastEndAt: 0,
    ended: false,
  };
};

/** Liga os listeners. Idempotente; seguro fora do browser (SSR/testes). */
export const installUiContext = (): void => {
  if (typeof window === 'undefined' || (window as any).__abbiamoUiCtx) return;
  (window as any).__abbiamoUiCtx = true;
  // Vazio de propósito: a carga inicial da página (aberta direto pela URL,
  // ex. /care/disputes?disputeId=…) também vira `navigation: /tela`, raiz das
  // requests do primeiro render. Com o pathname aqui elas saíam como
  // `background` e o backend virava a raiz do trace.
  lastScreen = '';
  try {
    Sentry.setTag('session_id', currentSessionId());
  } catch {
    /* Sentry desligado */
  }

  window.addEventListener(
    'click',
    e => {
      const hit = labelled(e.target as Element);
      if (!hit) return;
      lastClick = { label: hit.label, at: Date.now() };
      begin('click', hit.label, {
        'abbiamo.ui.element': selectorOf(hit.node) || '',
      });
    },
    true,
  );
  window.addEventListener(
    'submit',
    e => {
      const form = e.target as HTMLFormElement | null;
      const label =
        form?.getAttribute('aria-label') ||
        form?.getAttribute('data-action') ||
        form?.getAttribute('name') ||
        form?.id ||
        lastClick?.label;
      begin('submit', label ? clean(label) : undefined, {
        'abbiamo.ui.element': selectorOf(form) || 'form',
        'abbiamo.ui.fields': form?.elements?.length ?? 0,
      });
    },
    true,
  );
  window.addEventListener(
    'keydown',
    e => {
      if (e.key !== 'Enter') return;
      const target = e.target as Element | null;
      const hit = labelled(target);
      const field = target?.getAttribute?.('name') || target?.getAttribute?.('placeholder');
      begin('enter', hit?.label || (field ? clean(field) : undefined), {
        'abbiamo.ui.element': selectorOf(hit?.node || target) || '',
      });
    },
    true,
  );
  // Arquivo escolhido: o clique em "Anexar foto" só abre o seletor; a
  // request de upload sai quando o usuário escolhe o arquivo, segundos
  // depois. O nome da interação é o do botão que abriu o seletor.
  window.addEventListener(
    'change',
    e => {
      const input = e.target as HTMLInputElement | null;
      if (input?.tagName?.toLowerCase() !== 'input' || input.type !== 'file') return;
      const own =
        input.getAttribute('aria-label') || input.getAttribute('data-action') || input.name;
      const clicked =
        lastClick && Date.now() - lastClick.at <= UPLOAD_AFTER_CLICK_MS
          ? lastClick.label
          : undefined;
      const label = own || clicked || input.accept || undefined;
      const files = Array.from(input.files || []);
      begin('upload', label ? clean(label) : undefined, {
        'abbiamo.ui.element': selectorOf(input) || 'input[type=file]',
        'abbiamo.ui.files': files.length,
        'abbiamo.ui.file_types': Array.from(new Set(files.map(f => f.type || '?')))
          .join(',')
          .slice(0, MAX),
        'abbiamo.ui.file_bytes': files.reduce((n, f) => n + (f.size || 0), 0),
      });
    },
    true,
  );
};

/** `/care/disputes/8ec52068-…/x` → `/care/disputes/{id}/x` (UUID, número, hex longo, token opaco). */
const maskIds = (path: string): string =>
  path
    .split('/')
    .map(seg =>
      /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+|[0-9a-f]{16,}|[A-Za-z0-9_-]{20,})$/i.test(
        seg,
      )
        ? '{id}'
        : seg,
    )
    .join('/');

/** A tela atual: rota do react-router com os ids trocados por `{id}`. */
export const currentScreen = (): string => {
  if (typeof window === 'undefined') return '';
  return maskIds(window.location.pathname);
};

const joinUrl = (base?: string, url?: string): string => {
  if (!url) return base || '';
  if (/^https?:\/\//i.test(url) || !base) return url;
  return `${base.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
};

/**
 * O span `call POST /v2/dashboard/care/disputes/{id}/attachments` da request
 * que está saindo, filho da interação: é o lado do browser da chamada, o par
 * do `handle POST …` que o dashboard-api grava. O `traceparent` aponta pra
 * ele, então o backend pendura embaixo da chamada, não da interação.
 */
const openCallSpan = (it: Interaction, config: any): any => {
  if (!it.span || typeof (Sentry as any).withActiveSpan !== 'function') return undefined;
  try {
    const method = String(config?.method || 'GET').toUpperCase();
    const full = joinUrl(config?.baseURL, config?.url);
    const host = (full.match(/^https?:\/\/([^/?#]+)/i) || [])[1] || window.location.host;
    const path = (full.replace(/^https?:\/\/[^/?#]+/i, '').split(/[?#]/)[0] || '/').replace(
      /^\/(pr-\d+|dev-[a-z0-9-]+|sandbox|prod)(?=\/)/,
      '',
    );
    return (Sentry as any).withActiveSpan(it.span, () =>
      Sentry.startInactiveSpan({
        name: `call ${method} ${maskIds(path)}`,
        op: 'http.client',
        startTime: serverNow(),
        attributes: {
          'http.request.method': method,
          'url.full': full.split('#')[0].slice(0, 500),
          'server.address': host,
          'abbiamo.ui.screen': currentScreen(),
          'abbiamo.ui.action': it.label,
          'abbiamo.session_id': currentSessionId(),
        },
      }),
    );
  } catch {
    return undefined;
  }
};

const closeCallSpan = (span: any, status?: number, error?: unknown): void => {
  if (!span) return;
  try {
    // status 0 = a resposta nem chegou (rede, CORS, timeout): não é um status.
    const hasStatus = typeof status === 'number' && status > 0;
    if (hasStatus) span.setAttribute?.('http.response.status_code', status);
    const failed = error !== undefined || (hasStatus && status >= 400);
    if (failed) {
      const message = hasStatus
        ? String(status)
        : ((error as any)?.code as string) || ((error as any)?.message as string) || 'network';
      span.setAttribute?.('error.type', message.slice(0, 80));
      // 2 = SPAN_STATUS_ERROR no SDK do Sentry
      span.setStatus?.({ code: 2, message: message.slice(0, 80) });
    }
    span.end?.(serverNow());
  } catch {
    /* noop */
  }
};

/** A interação a que uma request que começa AGORA pertence, se alguma. */
const interactionFor = (now: number): Interaction | undefined => {
  const it = current;
  if (!it) return undefined;
  if (now - it.startedAt > MAX_INTERACTION_MS) return undefined;
  if (now - it.startedAt <= it.joinMs) return it;
  if (it.inflight > 0) return it;
  if (it.lastEndAt && now - it.lastEndAt <= CASCADE_GAP_MS) return it;
  return undefined;
};

/**
 * Os headers pra request que está saindo agora. Se ela pertence a uma
 * interação, entra no trace dela (e conta como em andamento até
 * `uiContextRequestDone`).
 */
export const uiContextHeaders = (config?: any): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  const now = Date.now();
  const out: Record<string, string> = {
    [UI_SCREEN_HEADER]: headerSafe(currentScreen()),
    [UI_SESSION_HEADER]: currentSessionId(),
  };
  let it = interactionFor(now);
  // Mudou de tela e nenhuma interação aberta: o carregamento da tela é uma
  // interação própria (`navigation: /dispatch-rules`), raiz das requests do
  // mount. Sem isso elas iam parar no trace do último clique.
  if (!it && window.location.pathname !== lastScreen) {
    begin('navigation', currentScreen());
    it = current;
  }
  if (it) {
    out[UI_ACTION_HEADER] = headerSafe(it.label);
    // Pai = o span `call` desta request (filho da interação) quando o Sentry
    // exporta (flag 01); senão só o trace_id (flag 00) e o backend vira a raiz.
    const call = openCallSpan(it, config);
    const callSpanId: string | undefined = call?.spanContext?.()?.spanId;
    const parent = it.sampled && callSpanId ? callSpanId : it.spanId;
    out['traceparent'] = `00-${it.traceId}-${parent}-${it.sampled ? '01' : '00'}`;
    it.inflight += 1;
    if (config && typeof config === 'object') {
      config.__abbiamoInteraction = it;
      config.__abbiamoCallSpan = call;
    }
  } else {
    // Polling/timer: trace próprio, sem pai (flag 00: o backend vira a
    // raiz). Sem este header o Sentry mandaria o `sentry-trace` dele,
    // apontando pro último trace de interação — era assim que o polling
    // caía dentro do trace do clique anterior.
    out[UI_ACTION_HEADER] = 'background';
    out['traceparent'] = `00-${hex(16)}-${hex(8)}-00`;
  }
  // Quando saiu, pro relógio (toda resposta ensina, com ou sem interação).
  if (config && typeof config === 'object') config.__abbiamoSentAt = now;
  lastScreen = window.location.pathname;
  return out;
};

/**
 * A request terminou (sucesso ou erro): fecha o span `call` dela com o
 * status e fecha a cascata quando for a última.
 */
export const uiContextRequestDone = (
  config?: any,
  status?: number,
  error?: unknown,
  serverTime?: unknown,
): void => {
  // A hora do servidor na resposta ensina o deslocamento do relógio daqui.
  const t = Number(serverTime);
  if (Number.isFinite(t) && t > 0 && typeof config?.__abbiamoSentAt === 'number') {
    learnClock(config.__abbiamoSentAt, t);
  }
  if (config?.__abbiamoCallSpan) {
    closeCallSpan(config.__abbiamoCallSpan, status, error);
    config.__abbiamoCallSpan = undefined;
  }
  const it: Interaction | undefined = config?.__abbiamoInteraction;
  if (!it) return;
  it.inflight = Math.max(0, it.inflight - 1);
  it.lastEndAt = Date.now();
  if (it.inflight === 0) {
    window.setTimeout(() => {
      if (it.inflight === 0 && it === current) endInteraction(it);
      else if (it.inflight === 0) endInteraction(it);
    }, CASCADE_GAP_MS + 50);
  }
};
